import type { ItemProperties, POIPageField } from "@/types";
import type { ProjectText } from "@/features/project/useProjectText";

/**
 * Turning a raw POI property into the text a configured field renders.
 *
 * Pure, and separate from the component, because this is where all the tenant
 * configuration actually bites — prefixes, suffixes, separators, per-field
 * defaults — and it's the part worth testing directly.
 */

/**
 * `true` means "render the field, but it has no text" — a boolean property whose
 * whole contribution is its icon. `""` means "render nothing".
 */
export type FieldValue = string | true;

/**
 * Format one field's value.
 *
 * `translate` resolves prefix/suffix keys against the tenant's own strings; a key
 * the tenant hasn't authored contributes nothing, as in the legacy app.
 *
 * A literal `"null"` is treated as absent: that string is what Squidex stores in
 * the `default` slot when a tenant clears it, and it appears verbatim in real
 * project configs.
 *
 * A numeric `0` renders as "0". Legacy tested the raw value for truthiness, so a
 * zero fell through to the field's default and a count of none was indistinguish-
 * able from an unset field.
 */
export function formatFieldValue(
  field: POIPageField,
  value: unknown,
  translate: ProjectText
): FieldValue {
  const fallback = field.default === "null" ? "" : (field.default ?? "");

  if (value === null || value === undefined || value === "" || value === false) {
    return fallback;
  }

  // A boolean flag carries no text of its own. Legacy would have concatenated the
  // literal "true" had the field also been given a prefix or suffix, which is not
  // a thing anyone configured on purpose.
  if (value === true) return true;

  if (Array.isArray(value)) {
    if (value.length === 0) return fallback;

    const parts = value.map(String);
    const suffix = translate(field.suffix);
    const prefix = translate(field.prefix);

    // `spread*` decides whether the affix belongs to every entry or to the list:
    // "3 rooms, 2 rooms" versus "3, 2 rooms".
    const withSuffix = field.spreadSuffix
      ? parts.map((part) => part + suffix)
      : suffix
        ? [...parts, suffix]
        : parts;
    const withPrefix = field.spreadPrefix
      ? withSuffix.map((part) => prefix + part)
      : prefix
        ? [prefix, ...withSuffix]
        : withSuffix;

    return withPrefix.join(field.separator || " ");
  }

  const text = String(value);
  if (text === "null") return fallback;

  return translate(field.prefix) + text + translate(field.suffix);
}

/**
 * Whether a block of fields has anything to show for this POI.
 *
 * A block whose fields are all empty renders nothing at all — not even its title,
 * which would otherwise sit above a blank gap.
 */
export function hasAnyFieldData(
  fields: POIPageField[] | undefined,
  properties: ItemProperties | undefined
): boolean {
  if (!fields?.length || !properties) return false;

  return fields.some((field) => {
    if (!field.field) return false;
    const value = properties[field.field];

    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) {
      return value.some(
        (entry) =>
          entry !== null && entry !== undefined && String(entry).trim() !== ""
      );
    }
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });
}
